// ---------------------------------------------------------------------------
// DW.2: useAgentTasks — Hook for managing agent task lifecycle
// ---------------------------------------------------------------------------
// Provides: list tasks, create task, approve/reject, polling for updates.
// Level 0: all actions require human approval.

import { useState, useCallback, useRef, useEffect } from "react";
import { apiFetch } from "../lib/api-client";

// ---------------------------------------------------------------------------
// Types (frontend mirror of packages/ai/src/agents/types.ts)
// ---------------------------------------------------------------------------

export type AgentState = "idle" | "thinking" | "action_proposed" | "approved" | "executing" | "complete" | "failed" | "rejected";
export type AgentType = "lead_qualifier" | "review_responder" | "appointment_reminder" | "email_drafter" | "data_enrichment";
export type AgentExecutionMode = "auto_execute";

export interface AgentTask {
  id: string;
  tenantId: string;
  agentType: AgentType;
  goal: string;
  state: AgentState;
  proposedAction: {
    toolName: string;
    arguments: Record<string, unknown>;
    reasoning: string;
    confidence: number;
    aiGenerated: true;
  } | null;
  result: {
    success: boolean;
    output: Record<string, unknown>;
    actionsExecuted: number;
    totalDurationMs: number;
  } | null;
  createdAt: string;
  updatedAt: string;
  executionMode: AgentExecutionMode;
  executionMessage: string;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 3_000;
const TERMINAL_STATES: ReadonlySet<AgentState> = new Set(["approved", "complete", "failed", "rejected"]);

function isTerminalTask(task: AgentTask): boolean {
  return TERMINAL_STATES.has(task.state);
}

export function useAgentTasks() {
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    try {
      const data = await apiFetch<{ tasks: AgentTask[] }>("/ai/agents/tasks");
      setTasks(data.tasks);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch tasks");
    }
  }, []);

  const createTask = useCallback(async (
    agentType: AgentType,
    goal: string,
    context: Record<string, unknown> = {},
  ): Promise<AgentTask | null> => {
    setIsLoading(true);
    try {
      const data = await apiFetch<{ task: AgentTask }>("/ai/agents/tasks", {
        method: "POST",
        body: JSON.stringify({ agentType, goal, context }),
      });
      setTasks((prev) => [data.task, ...prev]);
      setError(null);
      return data.task;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create task");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const approveAction = useCallback(async (
    taskId: string,
    modifiedArguments?: Record<string, unknown>,
  ): Promise<void> => {
    try {
      const data = await apiFetch<{ task: AgentTask }>(`/ai/agents/tasks/${taskId}/approve`, {
        method: "POST",
        body: JSON.stringify({ approved: true, modifiedArguments }),
      });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? data.task : t)));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to approve action");
    }
  }, []);

  const rejectAction = useCallback(async (
    taskId: string,
    reason: string,
  ): Promise<void> => {
    try {
      const data = await apiFetch<{ task: AgentTask }>(`/ai/agents/tasks/${taskId}/approve`, {
        method: "POST",
        body: JSON.stringify({ approved: false, rejectionReason: reason }),
      });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? data.task : t)));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reject action");
    }
  }, []);

  // Fetch tasks on mount
  useEffect(() => {
    fetchTasks();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track active tasks via ref to avoid effect churn from tasks array changes
  const hasActiveRef = useRef(false);
  hasActiveRef.current = tasks.some((task) => !isTerminalTask(task));

  // Poll for task updates — effect only depends on fetchTasks (stable)
  useEffect(() => {
    const id = setInterval(() => {
      if (hasActiveRef.current) fetchTasks();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchTasks]);

  // Derived state
  const pendingApproval = tasks.filter((t) => t.state === "action_proposed");
  const activeTasks = tasks.filter((task) => !isTerminalTask(task));
  const completedTasks = tasks.filter((task) => isTerminalTask(task));

  return {
    tasks,
    pendingApproval,
    activeTasks,
    completedTasks,
    isLoading,
    error,
    fetchTasks,
    createTask,
    approveAction,
    rejectAction,
  };
}

export { isTerminalTask };
